// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useState } from 'react';
import { Check, Info, Upload } from 'lucide-react';
import { createOrganizationProfile, updateOrganization, createPaymentAccount } from '../../lib/backOfficeApi';
import { toast } from '../../lib/buses';
import { Field, Input, Select, Textarea } from '../../ui/Form';
import { Modal } from '../../ui/Modal';
import { WizardFooter, WizardSteps } from '../../ui/Wizard';

const ONBOARD_STEPS = [
  { id: 'business',  label: 'Business' },
  { id: 'contacts',  label: 'Contacts' },
  { id: 'kyc',       label: 'KYC docs' },
  { id: 'settlement',label: 'Settlement' },
];
const ONBOARD_CATEGORIES = ['Sports', 'Music & Concerts', 'Festivals', 'Cinema', 'Conferences', 'Cultural', 'Tournaments', 'Other'];

export const OnboardOrganizerModal = ({ onClose, onCreate }) => {
  const [step, setStep] = useState('business');
  // Business
  const [legalName, setLegalName] = useState('');
  const [tradingName, setTradingName] = useState('');
  const [country, setCountry] = useState('Uganda');
  const [orgType, setOrgType] = useState('company');
  const [website, setWebsite] = useState('');
  const [description, setDescription] = useState('');
  const [eventCategories, setEventCategories] = useState([]);
  // Contacts
  const [primary, setPrimary] = useState({ name: '', email: '', phone: '', role: 'Founder/Director' });
  const [finance, setFinance] = useState({ name: '', email: '', phone: '' });
  const [accountManager, setAccountManager] = useState('');
  // KYC
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [tin, setTin] = useState('');
  const [docCert, setDocCert] = useState(false);
  const [docDirectors, setDocDirectors] = useState(false);
  const [docTax, setDocTax] = useState(false);
  const [docId, setDocId] = useState(false);
  // Settlement
  const [bankName, setBankName] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [bankBranch, setBankBranch] = useState('');
  const [tier, setTier] = useState('Tier 3');
  const [commissionPct, setCommissionPct] = useState(8);
  const [payoutSchedule, setPayoutSchedule] = useState('T+2');
  const [submitting, setSubmitting] = useState(false);

  const STEPS = ONBOARD_STEPS;

  const CATEGORIES = ONBOARD_CATEGORIES;
  const toggleCategory = (c) => setEventCategories(cs => cs.includes(c) ? cs.filter(x => x !== c) : [...cs, c]);

  const canBusiness  = legalName.trim() && country && eventCategories.length > 0;
  const canContacts  = primary.name.trim() && primary.email.trim() && primary.phone.trim() && accountManager;
  const canKyc       = registrationNumber.trim() && tin.trim() && docCert && docId;
  const canSettle    = bankName.trim() && bankAccount.trim() && commissionPct > 0;
  const canSubmit    = canBusiness && canContacts && canKyc && canSettle;

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const organizerName = tradingName.trim() || legalName.trim();
      const profile = await createOrganizationProfile({
        organizerName,
        ...(website.trim() ? { website: website.trim() } : {}),
        bio: description.trim(),
        // The backend stores `description` as a string column and rejects
        // objects/arrays; the back-office reads it back via parseMaybeJson, so
        // serialise the structured profile to a JSON string.
        description: JSON.stringify({
          legalName: legalName.trim(),
          tradingName: tradingName.trim(),
          country,
          orgType,
          eventCategories,
          primaryContact: primary,
          financeContact: finance,
          accountManager,
          registrationNumber,
          tin,
          documents: {
            certificateOfIncorporation: docCert,
            directorsRegister: docDirectors,
            taxClearance: docTax,
            directorIds: docId,
          },
          settlement: {
            bankName,
            bankAccount,
            bankBranch,
            tier,
            commissionPct,
            payoutSchedule,
          },
        }),
      });
      // createOrganizationProfile mints a real organization (see organizationProfileRoutes.ts)
      // and returns its id. Everything above this point was ALSO getting silently stuffed into
      // the profile's `description` JSON blob and nowhere else — the real Profile tab reads
      // org.metadata (not this blob) and the real Banking tab reads PaymentAccount rows (not
      // this blob either), so business details and the bank account never actually showed up
      // anywhere a human would look for them. Write them to the real places too.
      const organizationId = profile?.organizationId;
      const warnings = [];
      if (organizationId) {
        // Field names match OrgProfileTab.jsx's FIELDS list exactly — that's the real reader.
        await updateOrganization({
          organizationId,
          data: {
            metadata: {
              email: primary.email.trim(),
              phone: primary.phone.trim(),
              website: website.trim(),
              legalName: legalName.trim(),
              taxId: tin.trim(),
              regNo: registrationNumber.trim(),
              bio: description.trim(),
            },
          },
        }).catch((e) => warnings.push(`Business profile: ${e?.message || 'not saved'}`));

        await createPaymentAccount({
          organizationId,
          name: `${organizerName} · ${bankName}`,
          type: 'bank_account',
          bankName,
          accountNumber: bankAccount,
          currency: 'UGX',
          isDefault: true,
        }).catch((e) => warnings.push(`Payout account: ${e?.message || 'not saved'}`));
      } else {
        warnings.push('No organization id returned — business profile and payout account were not saved.');
      }

      if (onCreate) onCreate(profile);
      if (warnings.length) {
        toast('Organizer created with gaps', 'warning', warnings.join(' · '));
      } else {
        toast('Organizer profile created', 'success', `${organizerName} · profile, payout account, and org saved`);
      }
      onClose();
    } catch (error) {
      toast('Organizer profile failed', 'error', error?.message || 'Backend rejected the profile');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={true} onClose={onClose} width="max-w-[760px]" title="Create organizer profile" sub="Capture the organizer profile fields exposed by the backend. Full organization account creation remains handled by the auth organization flow.">
      <WizardSteps steps={STEPS} current={step} />

      {step === 'business' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Legal entity name" required><Input value={legalName} onChange={e => setLegalName(e.target.value)} placeholder="Akibua Sports Ltd" /></Field>
            <Field label="Trading name" optional sub="Customer-facing name if different from legal"><Input value={tradingName} onChange={e => setTradingName(e.target.value)} placeholder="Akibua Sports" /></Field>
            <Field label="Country of incorporation" required>
              <Select value={country} onChange={e => setCountry(e.target.value)} options={['Uganda','Kenya','Tanzania','Rwanda','Other']} />
            </Field>
            <Field label="Entity type" required>
              <Select value={orgType} onChange={e => setOrgType(e.target.value)}>
                <option value="company">Limited company</option>
                <option value="ngo">NGO / Non-profit</option>
                <option value="sole">Sole proprietor</option>
                <option value="partnership">Partnership</option>
                <option value="govt">Government / Public body</option>
              </Select>
            </Field>
            <Field label="Website" optional><Input value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://akibua.ug" /></Field>
          </div>
          <Field label="What kind of events?" required sub="Pick all that apply · drives default risk profile and category routing">
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map(c => {
                const on = eventCategories.includes(c);
                return (
                  <button key={c} onClick={() => toggleCategory(c)} className={`h-7 px-2.5 text-[11.5px] rounded-full border ${on ? 'bg-[#1F2937] text-white border-[#1F2937]' : 'bg-white text-[#1F2937] border-[#E6E4E1] hover:bg-[#FBFAF7]'}`}>
                    {on && <Check className="inline w-3 h-3 mr-1 -mt-0.5" />}{c}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Description" optional sub="Short bio · shown on organizer page">
            <Textarea rows={2} value={description} onChange={e => setDescription(e.target.value)} placeholder="Akibua promotes UPL football fixtures and friendlies across Uganda…" />
          </Field>
        </div>
      )}

      {step === 'contacts' && (
        <div className="space-y-3">
          <div>
            <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">Primary contact</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Full name" required><Input value={primary.name} onChange={e => setPrimary({ ...primary, name: e.target.value })} placeholder="Daniel Mwesigwa" /></Field>
              <Field label="Role" required>
                <Select value={primary.role} onChange={e => setPrimary({ ...primary, role: e.target.value })} options={['Founder/Director', 'CEO/MD', 'Events Manager', 'General Manager', 'Operations Lead']} />
              </Field>
              <Field label="Email" required sub="Login email · welcome instructions sent here"><Input type="email" value={primary.email} onChange={e => setPrimary({ ...primary, email: e.target.value })} placeholder="daniel@akibua.ug" /></Field>
              <Field label="Phone" required><Input value={primary.phone} onChange={e => setPrimary({ ...primary, phone: e.target.value })} placeholder="+256 700 000 000" /></Field>
            </div>
          </div>
          <div className="border-t border-[#EEEDEA] pt-3">
            <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">Finance contact <span className="font-normal normal-case text-[#9CA3AF]">(optional · for payouts and tax)</span></div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Name"><Input value={finance.name} onChange={e => setFinance({ ...finance, name: e.target.value })} placeholder="Esther Nakimuli" /></Field>
              <Field label="Email"><Input type="email" value={finance.email} onChange={e => setFinance({ ...finance, email: e.target.value })} placeholder="finance@akibua.ug" /></Field>
              <Field label="Phone"><Input value={finance.phone} onChange={e => setFinance({ ...finance, phone: e.target.value })} placeholder="+256 700 000 000" /></Field>
            </div>
          </div>
          <div className="border-t border-[#EEEDEA] pt-3">
            <Field label="TKT account manager" required sub="Internal owner for the relationship">
              <Select value={accountManager} onChange={e => setAccountManager(e.target.value)}>
                <option value="">Assign account manager…</option>
                <option>Brian O. (Account mgr)</option>
                <option>Esther N. (Risk)</option>
                <option>Victor S. (Ops lead)</option>
                <option>Lillian K. (Finance)</option>
                <option>Mariam K. (Comms)</option>
              </Select>
            </Field>
          </div>
        </div>
      )}

      {step === 'kyc' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Company registration number" required sub="URSB / equivalent"><Input value={registrationNumber} onChange={e => setRegistrationNumber(e.target.value)} placeholder="URSB-2024-9817" /></Field>
            <Field label="Tax Identification Number (TIN)" required><Input value={tin} onChange={e => setTin(e.target.value)} placeholder="1000-XXX-XXX" /></Field>
          </div>
          <div>
            <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">Required documents</div>
            <div className="space-y-1.5">
              {[
                { id: 'cert', label: 'Certificate of incorporation', checked: docCert, setter: setDocCert, required: true },
                { id: 'directors', label: 'Directors / shareholders register', checked: docDirectors, setter: setDocDirectors },
                { id: 'tax', label: 'Tax clearance certificate', checked: docTax, setter: setDocTax },
                { id: 'id', label: 'Directors\' national IDs / passports', checked: docId, setter: setDocId, required: true },
              ].map(d => (
                <div key={d.id} className={`flex items-center justify-between px-3 py-2 rounded-md border ${d.checked ? 'border-[#1F6A3A] bg-[#F0F9F2]' : 'border-[#E6E4E1] bg-white'}`}>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className={`w-7 h-7 rounded-full ${d.checked ? 'bg-[#1F6A3A] text-white' : 'bg-[#F2F1ED] text-[#9CA3AF]'} flex items-center justify-center flex-shrink-0`}>{d.checked ? <Check className="w-3.5 h-3.5" strokeWidth={2.5} /> : <Upload className="w-3.5 h-3.5" />}</div>
                    <div className="min-w-0">
                      <div className="text-[12px] font-medium text-[#1F2937]">{d.label}{d.required && <span className="text-[#9B1F1F] ml-1">*</span>}</div>
                      <div className="text-[10.5px] text-[#6B7280] mt-0.5">{d.checked ? 'Uploaded · pending review' : 'PDF / JPG · max 5 MB'}</div>
                    </div>
                  </div>
                  {!d.checked ? (
                    <button onClick={() => d.setter(true)} className="h-7 px-2.5 text-[11px] font-medium rounded-md bg-white border border-[#E6E4E1] text-[#1F2937] hover:bg-[#F2F1ED] inline-flex items-center gap-1"><Upload className="w-3 h-3" />Upload</button>
                  ) : (
                    <button onClick={() => d.setter(false)} className="h-7 px-2.5 text-[11px] font-medium rounded-md text-[#6B7280] hover:bg-[#F2F1ED]">Remove</button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-[#FFF7E6] border border-[#FAE2A8] rounded-md px-3 py-2.5 text-[11.5px] text-[#8A5A12] flex items-start gap-2">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>KYC review takes 24–48h. The organizer can finish profile setup in the meantime but cannot publish events until KYC is approved.</div>
          </div>
        </div>
      )}

      {step === 'settlement' && (
        <div className="space-y-3">
          <div>
            <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">Bank account for payouts</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Bank name" required>
                <Select value={bankName} onChange={e => setBankName(e.target.value)}>
                  <option value="">Choose bank…</option>
                  {['Stanbic Bank Uganda','Centenary Bank','Equity Bank','DFCU Bank','Bank of Africa','Absa Bank','Standard Chartered'].map(b => <option key={b}>{b}</option>)}
                </Select>
              </Field>
              <Field label="Account number" required><Input value={bankAccount} onChange={e => setBankAccount(e.target.value)} placeholder="9030001234567" /></Field>
              <Field label="Branch" optional><Input value={bankBranch} onChange={e => setBankBranch(e.target.value)} placeholder="Kampala Main" /></Field>
            </div>
          </div>
          <div className="border-t border-[#EEEDEA] pt-3">
            <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">Commercial terms</div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Risk tier" sub="Drives velocity limits, KYC depth, recon cadence">
                <Select value={tier} onChange={e => setTier(e.target.value)}>
                  <option>Tier 1</option><option>Tier 2</option><option>Tier 3</option>
                </Select>
              </Field>
              <Field label="Platform commission" required sub="Per gross ticket sale">
                <div className="relative"><Input type="number" value={commissionPct} onChange={e => setCommissionPct(parseFloat(e.target.value) || 0)} /><span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-[#6B7280]">%</span></div>
              </Field>
              <Field label="Payout schedule" sub="When proceeds are released">
                <Select value={payoutSchedule} onChange={e => setPayoutSchedule(e.target.value)}>
                  <option value="T+0">Same-day (T+0)</option>
                  <option value="T+1">Next business day (T+1)</option>
                  <option value="T+2">T+2 (default)</option>
                  <option value="weekly">Weekly</option>
                  <option value="manual">Manual release only</option>
                </Select>
              </Field>
            </div>
          </div>
          <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md p-3">
            <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-2">On submit</div>
            <ul className="text-[11.5px] text-[#4B5563] space-y-1 list-disc list-inside">
              <li>A real organization is created (owned by you until you invite {primary.name || 'the primary contact'} via Team)</li>
              <li>Business details (email, phone, website, legal name, TIN, reg. no., bio) save to the organizer's real Profile tab</li>
              <li>The bank account saves as a real payout account under Banking &amp; Payouts</li>
              <li>KYC documents are NOT uploaded here — this only records that {docCert && docId ? 'you have' : 'you will collect'} them offline. Attach the real files under the KYC Documents tab afterward.</li>
              <li>Account manager <span className="text-[#1F2937]">{accountManager || '—'}</span> remains an internal profile field</li>
            </ul>
          </div>
        </div>
      )}

      <WizardFooter
        onBack={step !== 'business' ? () => setStep(STEPS[STEPS.findIndex(s => s.id === step) - 1].id) : null}
        onCancel={onClose}
        onNext={() => {
          const idx = STEPS.findIndex(s => s.id === step);
          if (idx < STEPS.length - 1) {
            if ((step === 'business' && canBusiness) || (step === 'contacts' && canContacts) || (step === 'kyc' && canKyc)) setStep(STEPS[idx + 1].id);
          } else if (canSubmit) submit();
        }}
        nextLabel={step === 'settlement' ? (submitting ? 'Creating profile…' : 'Create profile') : 'Continue'}
        nextDisabled={submitting || (step === 'business' ? !canBusiness : step === 'contacts' ? !canContacts : step === 'kyc' ? !canKyc : !canSettle)}
        primaryIcon={step === 'settlement' ? Check : null}
      />
    </Modal>
  );
};

/* ----------------------------------------------------------------------------
   CREATE BOX OFFICE WIZARD — replaces the inline modal in BoxOfficesListPage
   Covers scope (system/tournament/event), basics, tickets, payments, operators+credentials, printing
   ---------------------------------------------------------------------------- */
